class TracklistPlayer {
  constructor(el) {
    this.el = el;
    this.audio = null;
    this.currentIndex = null;
    this.tracks = [];
    this.albums = [];
    this.player = null;
    this.init();
  }

  async init() {
    await this.fetchTracks();
    if (this.tracks.length || this.albums.length) {
      this.renderTracks();
      this.createMiniPlayer();
    }
  }

  async fetchTracks() {
    const loadingEl = this.el.querySelector('.tracklist-loading');
    try {
      if (this.el.dataset.itunesIds) {
        const ids = [...new Set(JSON.parse(this.el.dataset.itunesIds).map(id => String(id).trim()).filter(Boolean))];
        this.albums = await Promise.all(ids.map(async (id, index) => {
          const album = { title: `Album ${index + 1}`, tracks: [] };
          try {
            Object.assign(album, await this.fetchFromItunes(id));
          } catch (error) {
            album.error = 'Could not load this album. Please try again later.';
          }
          return album;
        }));
        this.tracks = this.albums.flatMap(album => album.tracks);
      } else if (this.el.dataset.itunesId) {
        this.tracks = (await this.fetchFromItunes(this.el.dataset.itunesId)).tracks;
      } else if (this.el.dataset.deezerId) {
        await this.fetchFromDeezer(this.el.dataset.deezerId);
      }
      if (loadingEl) {
        if (this.tracks.length || this.albums.length) loadingEl.remove();
        else loadingEl.textContent = 'No tracks found.';
      }
    } catch (e) {
      console.error('[Tracklist] Load failed:', e);
      if (loadingEl) loadingEl.textContent = 'Could not load tracklist.';
    }
  }

  fetchFromItunes(id) {
    return new Promise((resolve, reject) => {
      if (!/^\d+$/.test(id)) { reject(new Error('Invalid album ID')); return; }
      const cb = `itunes_cb_${Date.now()}_${TracklistPlayer.requestId++}`;
      const script = document.createElement('script');
      script.src = `https://itunes.apple.com/lookup?id=${encodeURIComponent(id)}&entity=song&limit=200&callback=${cb}`;
      const cleanup = () => { clearTimeout(timeout); delete window[cb]; script.remove(); };
      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error('iTunes JSONP timeout'));
      }, 15000);

      window[cb] = (data) => {
        cleanup();
        const results = data.results || [];
        const collection = results.find(r => r.wrapperType === 'collection');
        const tracks = results
          .filter(r => r.wrapperType === 'track' && String(r.collectionId) === String(id))
          .map(t => ({
            number: t.trackNumber,
            disc: t.discNumber || 1,
            name: t.trackName,
            artist: t.artistName,
            durationMs: t.trackTimeMillis || 0,
            preview: t.previewUrl || null,
          }));
        resolve({ title: collection ? collection.collectionName : 'Album', tracks });
      };

      script.onerror = () => {
        cleanup();
        reject(new Error('iTunes JSONP failed'));
      };

      document.body.appendChild(script);
    });
  }

  fetchFromDeezer(id) {
    return new Promise((resolve, reject) => {
      const cb = `deezer_cb_${Date.now()}`;
      const script = document.createElement('script');
      script.src = `https://api.deezer.com/album/${id}/tracks?callback=${cb}`;

      const cleanup = () => { delete window[cb]; script.remove(); };

      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error('Deezer JSONP timeout'));
      }, 8000);

      window[cb] = (data) => {
        clearTimeout(timeout);
        cleanup();
        if (data.error) { reject(new Error(`Deezer API error: ${data.error.type}`)); return; }
        this.tracks = (data.data || []).map((t, i) => ({
          number: i + 1,
          disc: t.disk_number || 1,
          name: t.title,
          artist: t.artist ? t.artist.name : '',
          durationMs: (t.duration || 0) * 1000,
          preview: t.preview || null,
        }));
        resolve();
      };

      script.onerror = () => {
        clearTimeout(timeout);
        cleanup();
        reject(new Error('Deezer JSONP failed'));
      };

      document.body.appendChild(script);
    });
  }

  formatTime(ms) {
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  }

  iconPlay() {
    return `<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M3 2l10 6-10 6V2z"/></svg>`;
  }

  iconPause() {
    return `<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M3 2h4v12H3zm6 0h4v12H9z"/></svg>`;
  }

  renderTracks() {
    const container = this.el.querySelector('.tracklist-tracks');
    if (!container) return;

    let html = '';
    let trackIndex = 0;
    const escape = value => String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
    const groups = this.albums.length ? this.albums : [{ tracks: this.tracks }];
    groups.forEach((album, albumIndex) => {
      let currentDisc = null;
      const totalDiscs = Math.max(1, ...album.tracks.map(t => t.disc));
      if (this.albums.length) {
        html += `<details class="tracklist-album"${albumIndex === 0 ? ' open' : ''}><summary>${escape(album.title)}</summary>`;
        if (!album.tracks.length) html += `<p class="tracklist-error">${escape(album.error || 'No tracks found for this album.')}</p>`;
      }

    album.tracks.forEach((track) => {
      const i = trackIndex++;
      if (track.disc !== currentDisc) {
        currentDisc = track.disc;
        if (totalDiscs > 1) {
          html += `<div class="tracklist-disc-header">Disc ${currentDisc}</div>`;
        }
      }

      const duration = track.preview ? '0:30' : (track.durationMs ? this.formatTime(track.durationMs) : '');

      html += `<div class="tracklist-track" data-index="${i}">
        <span class="tracklist-track-number">${track.number}</span>
        <div class="tracklist-track-info">
          <div class="tracklist-track-name">${escape(track.name)}</div>
          <div class="tracklist-track-artist">${escape(track.artist)}</div>
        </div>
        <span class="tracklist-track-duration">${duration}</span>
        ${track.preview
          ? `<button type="button" class="tracklist-track-play" data-index="${i}" aria-label="Play ${escape(track.name)}">${this.iconPlay()}</button>`
          : `<span class="tracklist-track-no-preview">${this.iconPlay()}</span>`
        }
      </div>`;
    });
      if (this.albums.length) html += '</details>';
    });

    container.innerHTML = html;
    container.querySelectorAll('.tracklist-track-play').forEach(btn => {
      btn.addEventListener('click', () => this.playTrack(parseInt(btn.dataset.index)));
    });
  }

  createMiniPlayer() {
    if (document.querySelector('.tracklist-mini-player')) return;

    const p = document.createElement('div');
    p.className = 'tracklist-mini-player';
    p.innerHTML = `
      <div class="tracklist-mini-player-info">
        <div class="tracklist-mini-player-name"></div>
        <div class="tracklist-mini-player-artist"></div>
      </div>
      <div class="tracklist-mini-player-controls">
        <button class="tracklist-mini-player-btn js-prev" aria-label="Previous">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path d="M4 4h2v12H4zm3.5 6l8.5 6V4z"/></svg>
        </button>
        <button class="tracklist-mini-player-btn js-playpause" aria-label="Pause">
          <svg class="icon-play" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style="display:none"><path d="M5 3l14 9-14 9V3z"/></svg>
          <svg class="icon-pause" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M6 4h4v16H6zm8 0h4v16h-4z"/></svg>
        </button>
        <button class="tracklist-mini-player-btn js-next" aria-label="Next">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path d="M16 4h-2v12h2zm-3.5 6L4 4v12z"/></svg>
        </button>
      </div>
      <div class="tracklist-mini-player-progress-wrap">
        <span class="tracklist-mini-player-time js-time">0:00</span>
        <div class="tracklist-mini-player-progress">
          <div class="tracklist-mini-player-progress-bar"></div>
        </div>
        <span class="tracklist-mini-player-time js-duration">0:30</span>
      </div>
      <button class="tracklist-mini-player-close" aria-label="Close">&#x2715;</button>
    `;

    document.body.appendChild(p);
    this.player = p;

    p.querySelector('.js-playpause').addEventListener('click', () => this.togglePause());
    p.querySelector('.js-prev').addEventListener('click', () => this.playAdjacentTrack(-1));
    p.querySelector('.js-next').addEventListener('click', () => this.playAdjacentTrack(1));
    p.querySelector('.tracklist-mini-player-close').addEventListener('click', () => this.close());
  }

  playTrack(index) {
    const track = this.tracks[index];
    if (!track || !track.preview) return;

    if (this.audio) {
      this.audio.pause();
      this.setTrackState(this.currentIndex, false);
    }

    this.currentIndex = index;
    this.audio = new Audio(track.preview);
    this.audio.play();

    this.setTrackState(index, true);
    this.updatePlayerInfo(track);
    this.setPlayerIcon('pause');

    this.audio.addEventListener('timeupdate', () => {
      const pct = (this.audio.currentTime / this.audio.duration) * 100 || 0;
      this.player.querySelector('.tracklist-mini-player-progress-bar').style.width = `${pct}%`;
      this.player.querySelector('.js-time').textContent = this.formatTime(this.audio.currentTime * 1000);
    });

    this.audio.addEventListener('ended', () => {
      this.setTrackState(index, false);
      this.setPlayerIcon('play');
      this.playAdjacentTrack(1);
    });
  }

  togglePause() {
    if (!this.audio) return;
    if (this.audio.paused) {
      this.audio.play();
      this.setPlayerIcon('pause');
      this.setTrackState(this.currentIndex, true);
    } else {
      this.audio.pause();
      this.setPlayerIcon('play');
      this.setTrackState(this.currentIndex, false);
    }
  }

  playAdjacentTrack(dir) {
    if (this.currentIndex === null) return;
    if (dir > 0) {
      const next = this.tracks.findIndex((t, i) => i > this.currentIndex && t.preview);
      if (next !== -1) this.playTrack(next);
    } else {
      const candidates = this.tracks.slice(0, this.currentIndex).reverse();
      const offset = candidates.findIndex(t => t.preview);
      if (offset !== -1) this.playTrack(this.currentIndex - 1 - offset);
    }
  }

  close() {
    if (this.audio) {
      this.audio.pause();
      this.setTrackState(this.currentIndex, false);
      this.audio = null;
      this.currentIndex = null;
    }
    this.player.classList.remove('is-visible');
  }

  setTrackState(index, playing) {
    if (index === null) return;
    const row = this.el.querySelector(`.tracklist-track[data-index="${index}"]`);
    const btn = this.el.querySelector(`.tracklist-track-play[data-index="${index}"]`);
    if (!row || !btn) return;
    row.classList.toggle('is-playing', playing);
    btn.classList.toggle('is-playing', playing);
    btn.innerHTML = playing ? this.iconPause() : this.iconPlay();
    btn.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} ${this.tracks[index].name}`);
  }

  updatePlayerInfo(track) {
    this.player.querySelector('.tracklist-mini-player-name').textContent = track.name;
    this.player.querySelector('.tracklist-mini-player-artist').textContent = track.artist;
    this.player.querySelector('.js-duration').textContent = track.durationMs
      ? this.formatTime(track.durationMs)
      : '0:30';
    this.player.querySelector('.tracklist-mini-player-progress-bar').style.width = '0%';
    this.player.classList.add('is-visible');
  }

  setPlayerIcon(state) {
    this.player.querySelector('.icon-play').style.display = state === 'play' ? '' : 'none';
    this.player.querySelector('.icon-pause').style.display = state === 'pause' ? '' : 'none';
  }
}

TracklistPlayer.requestId = 0;

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.tracklist-player[data-itunes-ids], .tracklist-player[data-itunes-id], .tracklist-player[data-deezer-id]').forEach(el => {
    new TracklistPlayer(el);
  });
});
